import axios from "axios";
import { mockApi } from "./mockApi";
import { normalizeRegionName } from "../data/regionMapping";

const USE_MOCK = false;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEPOSITS_PATH = process.env.REACT_APP_DEPOSITS_PATH;
const REGION_AGG_PATH = process.env.REACT_APP_REGION_AGG_PATH;

const convertDMSToDecimal = (deg, min, sec) => {
    return deg + min / 60 + sec / 3600;
};

const getWasteGroup = (groupId) => {
    if (groupId === "Отходы горнорудной отрасли") return "mining";
    if (groupId === "Щелочные индустриальные отходы") return "alkaline";
    return "other";
};

const transformPointData = (point) => {
    const latitude = convertDMSToDecimal(
        point.latitude_deg,
        point.latitude_min,
        point.latitude_sec
    );
    const longitude = convertDMSToDecimal(
        point.longitude_deg,
        point.longitude_min,
        point.longitude_sec
    );

    // console.log('Raw point data:', point);

    const chemicalComposition = {};
    const sortedChemical = (point.chemical_composition || [])
        .filter((item) => item && item.percentage >= 0.05)
        .sort((a, b) => b.percentage - a.percentage);

    sortedChemical.slice(0, 8).forEach((item) => {
        chemicalComposition[item.substance_name] = Number(
            item.percentage.toFixed(1)
        );
    });

    const otherChemicalSum = sortedChemical
        .slice(8)
        .reduce((sum, item) => sum + item.percentage, 0);

    if (otherChemicalSum >= 0.05) {
        chemicalComposition["Другое"] = Number(otherChemicalSum.toFixed(1));
    }

    const mineralComposition = {};
    const sortedMinerals = (point.mineral_composition || [])
        .filter((item) => item && item.percentage >= 0.05)
        .sort((a, b) => b.percentage - a.percentage);

    sortedMinerals.slice(0, 8).forEach((item) => {
        mineralComposition[item.mineral_name] = Number(
            item.percentage.toFixed(1)
        );
    });

    const otherMineralSum = sortedMinerals
        .slice(8)
        .reduce((sum, item) => sum + item.percentage, 0);

    if (otherMineralSum >= 0.05) {
        mineralComposition["Другое"] = Number(otherMineralSum.toFixed(1));
    }

    const co2Absorption = {};
    (point.consumption || []).forEach((item) => {
        if (item && item.year && item.value !== undefined) {
            co2Absorption[item.year] = item.value;
        }
    });

    const wasteByYear = {};
    (point.consumption || []).forEach((item) => {
        if (item && item.year && item.waste !== undefined && item.waste !== null) {
            wasteByYear[item.year] = item.waste;
        }
    });

    // В исходной таблице встречается "Центральная Федеральный Округ".
    const federalDistrict = (
        point.region?.federal_district?.replace(" Федеральный Округ", "") || ""
    ).replace(/^Центральная$/, "Центральный");

    return {
        id: point.id,
        group: getWasteGroup(point.resource.waste_group),
        physicalProperties: {
            surfaceArea: point.surface_area || 0,
            bulkDensity: point.bulk_density || 0,
            specificMass: point.specific_mass || 0,
            moisture: point.moisture || 0,
            massLoss: point.mass_loss || 0,
        },
        coordinates: [latitude, longitude],
        name: point.name || "",
        region: normalizeRegionName(point.region?.name) || "",
        federalDistrict: federalDistrict || "",
        wasteType: point.resource.name.toLowerCase() || "unknown",
        wasteCount: point.volume,
        wasteByYear,
        chemicalComposition,
        mineralComposition,
        co2Absorption,
        co2Forecast: {
            baseline: co2Absorption,
            optimistic: { ...co2Absorption },
            pessimistic: { ...co2Absorption },
        },
        _original: point,
    };
};

const transformCO2AbsData = (data) => {
    // console.log('Raw CO2 data:', data);

    const result = {
        districts: {},
        regions: {},
    };

    const districts = Array.isArray(data.districts) ? data.districts : [];
    const regions = Array.isArray(data.regions) ? data.regions : [];

    districts.forEach((district) => {
        if (!district || !district.federal_district) return;

        const districtName = district.federal_district.replace(
            " Федеральный Округ",
            ""
        );
        result.districts[districtName] = {
            co2Absorption:
                Number(Number(district.total_first_consumption).toFixed(4)) ||
                0,
            pointsCount: Number(district.num_deposits) || 0,
        };
    });

    regions.forEach((region) => {
        if (!region || !region.region_name) return;

        result.regions[normalizeRegionName(region.region_name)] = {
            co2Absorption:
                Number(Number(region.total_first_consumption).toFixed(4)) || 0,
            pointsCount: Number(region.num_deposits) || 0,
        };
    });

    // console.log('Transformed CO2 data:', result);

    return result;
};

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

export const fetchPoints = async () => {
    if (USE_MOCK) {
        try {
            const response = await mockApi.fetchPoints();
            const transformedPoints = response.map((point) =>
                transformPointData(point)
            );
            return transformedPoints;
        } catch (error) {
            // console.error("Error in fetchPoints:", error);
            throw error;
        }
    }

    try {
        const response = await api.get(DEPOSITS_PATH);
        return response.data.map(transformPointData);
    } catch (error) {
        // console.error("Error fetching points:", error);
        throw error;
    }
};

export const fetchDepositDetail = async (id) => {
    const response = await api.get(`${DEPOSITS_PATH}/${id}`);
    return transformPointData(response.data);
};

export const fetchDatasets = async () => {
    const response = await api.get("/api/datasets");
    return response.data;
};

export const fetchDatasetsSummary = async () => {
    const response = await api.get("/api/datasets/summary");
    return response.data;
};

export const getDatasetFileUrl = (datasetId, fileId) =>
    `${API_BASE_URL}api/datasets/${datasetId}/files/${fileId}/download`;

export const getDatasetsExportUrl = () => `${API_BASE_URL}api/datasets/export.zip`;

export const fetchContent = async () => {
    const response = await api.get("/api/content");
    return response.data;
};

export const fetchDataSources = async () => {
    const response = await api.get("/api/data-sources");
    return response.data;
};

export const fetchCo2Data = async () => {
    if (USE_MOCK) {
        try {
            const data = await mockApi.fetchCO2AbsData();
            // console.log("Fetched CO2 data:", data);
            return data;
        } catch (error) {
            // console.error("Error fetching mock CO2 data:", error);
            throw error;
        }
    }

    try {
        const response = await api.get(REGION_AGG_PATH);
        return transformCO2AbsData(response.data);
    } catch (error) {
        // console.error("Error fetching CO2 data:", error);
        throw error;
    }
};
